import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, useTheme } from '@mui/material';

import { BarPieceItem } from './types';
import { tickFormatter } from './utils';
import { useBarChartController } from './useBarChartController';
import { renderLegend } from './renderLegend';
import { BuildSelectedRangeTooltipProps, BuildTooltipProps } from './buildRenderTooltip';

type BaseBarChartProps<T> = {
  items: BarPieceItem<T>[];
  buildTooltipProps: BuildTooltipProps<T>;
  shouldShowLegend?: boolean;
  getBarColor?(
    item: BarPieceItem<T>,
    items: BarPieceItem<T>[],
    totalValue: number,
    isRangeSelected: boolean,
    isBarInRange: boolean,
  ): string;
  roundedBarCorners?: boolean;
};

type BarChartWithSelection<T> = BaseBarChartProps<T> & {
  disableSelection?: false;
  buildSelectedRangeTooltipProps: BuildSelectedRangeTooltipProps<T>;
};

type BarChartWithoutSelection<T> = BaseBarChartProps<T> & {
  disableSelection: true;
};

export type BarChartProps<T> = BarChartWithSelection<T> | BarChartWithoutSelection<T>;

export const BarChart = <T,>(props: BarChartProps<T>) => {
  const {
    items,
    buildTooltipProps,
    shouldShowLegend = true,
    getBarColor,
    roundedBarCorners,
    disableSelection = false,
  } = props;

  const buildSelectedRangeTooltipProps = props.disableSelection
    ? undefined
    : props.buildSelectedRangeTooltipProps;

  const { palette } = useTheme();
  const {
    handleOnMouseDown,
    handleOnMouseMove,
    handleOnMouseUp,
    selectedIndexes,
    refAreaEndIndex,
    refAreaStartIndex,
    renderTooltip,
    totalValue,
  } = useBarChartController({ items, buildTooltipProps, buildSelectedRangeTooltipProps });

  const theme = useTheme();

  const { fontSize, fontWeight } = theme.typography.caption;

  /**
   * Calculate the fill color for a bar based on selection state
   */
  const getBarFillColor = (index: number): string => {
    if (disableSelection) {
      return palette.barChart.bar;
    }

    const isRangeSelected = selectedIndexes.length > 0;

    if (isRangeSelected) {
      return selectedIndexes.includes(index)
        ? palette.barChart.selectedBar
        : palette.barChart.notSelectedBar;
    }

    if (refAreaStartIndex !== null && refAreaEndIndex !== null) {
      const minIndex = Math.min(refAreaStartIndex, refAreaEndIndex);
      const maxIndex = Math.max(refAreaStartIndex, refAreaEndIndex);

      return index >= minIndex && index <= maxIndex
        ? palette.barChart.selectedBar
        : palette.barChart.notSelectedBar;
    }

    return palette.barChart.bar;
  };

  return (
    <Box sx={{ userSelect: 'none', width: '100%', height: '100%' }}>
      <ResponsiveContainer width='100%' height='100%'>
        <RechartsBarChart
          width={500}
          height={300}
          data={items}
          onMouseDown={disableSelection ? undefined : handleOnMouseDown}
          onMouseMove={disableSelection ? undefined : handleOnMouseMove}
          onMouseUp={disableSelection ? undefined : handleOnMouseUp}
        >
          <CartesianGrid vertical={false} />

          <XAxis dataKey='name' tickLine={false} tick={{ fontSize, fontWeight }} />
          <YAxis
            tickCount={4}
            tickFormatter={tickFormatter}
            tick={{ fontSize, fontWeight }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={35}
          />

          <Tooltip content={renderTooltip} cursor={{ fill: 'transparent' }} />

          {shouldShowLegend && <Legend content={renderLegend} name='Responses' />}

          <Bar
            dataKey='value'
            fill={palette.barChart.bar}
            animationDuration={0}
            barSize={12}
            radius={roundedBarCorners ? [2, 2, 0, 0] : undefined}
          >
            {items.map((entry, index) => {
              let fill = getBarFillColor(index);
              const isRangeSelected = !disableSelection && selectedIndexes.length > 0;
              const isBarInRange = !disableSelection && selectedIndexes.includes(index);

              const overriddenFillColor = getBarColor?.(
                entry,
                items,
                totalValue,
                isRangeSelected,
                isBarInRange,
              );
              if (overriddenFillColor) {
                fill = overriddenFillColor;
              }

              return (
                <Cell cursor={disableSelection ? 'default' : 'pointer'} fill={fill} key={index} />
              );
            })}
          </Bar>

          {!disableSelection && refAreaStartIndex !== null && refAreaEndIndex !== null ? (
            <ReferenceArea
              x1={items[Math.min(refAreaStartIndex, refAreaEndIndex)].name}
              x2={items[Math.max(refAreaStartIndex, refAreaEndIndex)].name}
              strokeOpacity={0.5}
              stroke={palette.barChart.bar}
              strokeWidth={2}
              fill={palette.barChart.bar}
            />
          ) : null}
        </RechartsBarChart>
      </ResponsiveContainer>
    </Box>
  );
};
